import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { User, Mail, Lock, Eye, EyeOff, ArrowRight, CheckCircle2, Image, X, PartyPopper } from 'lucide-react';
import { BASE_URL } from '../utils/api';
import { GoogleLogin } from '@react-oauth/google';
import logo512 from '../assets/logo512.png';

// Fix #1: API key from env var — never hardcoded
const getApiKey = () => process.env.REACT_APP_API_KEY || '';

const FALLBACK_AVATARS = [
  { "id": "gamepad", "url": "https://openmoji.org/data/color/svg/1F3AE.svg" },
  { "id": "bullseye", "url": "https://openmoji.org/data/color/svg/1F3AF.svg" },
  { "id": "dice", "url": "https://openmoji.org/data/color/svg/1F3B2.svg" },
  { "id": "circus", "url": "https://openmoji.org/data/color/svg/1F3AA.svg" },
  { "id": "rocket", "url": "https://openmoji.org/data/color/svg/1F680.svg" },
  { "id": "lightning", "url": "https://openmoji.org/data/color/svg/26A1.svg" },
  { "id": "fire", "url": "https://openmoji.org/data/color/svg/1F525.svg" },
  { "id": "diamond", "url": "https://openmoji.org/data/color/svg/1F48E.svg" }
];

const Signup = ({ onSignupSuccess }) => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [error, setError] = useState('');
  const [googleLoading, setGoogleLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    gamerName: '',
    avatarId: ''
  });

  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordStrength, setPasswordStrength] = useState('Weak');
  const [agreeTerms, setAgreeTerms] = useState(false);

  const [avatars, setAvatars] = useState([]);
  const [selectedAvatarId, setSelectedAvatarId] = useState(null);
  const [loadingAvatars, setLoadingAvatars] = useState(false);
  const [isCheckingAvailability, setIsCheckingAvailability] = useState(false);

  useEffect(() => {
    const fetchAvatars = async () => {
      setLoadingAvatars(true);
      try {
        const response = await fetch(`${BASE_URL}/Auth/avatars`, {
          headers: {
            'X-Api-Key': getApiKey() // Fix #1
          }
        });
        if (response.ok) {
          const data = await response.json();
          setAvatars(data);
        } else {
          setAvatars(FALLBACK_AVATARS);
        }
      } catch (err) {
        setAvatars(FALLBACK_AVATARS);
      } finally {
        setLoadingAvatars(false);
      }
    };
    fetchAvatars();
  }, []);

  const checkPasswordStrength = (pass) => {
    if (pass.length === 0) return setPasswordStrength('Weak');
    if (pass.length < 6) return setPasswordStrength('Weak');
    if (pass.length < 10) return setPasswordStrength('Fair');
    if (/[A-Z]/.test(pass) && /[0-9]/.test(pass) && /[^A-Za-z0-9]/.test(pass)) return setPasswordStrength('Strong');
    return setPasswordStrength('Fair');
  };

  const handlePasswordChange = (e) => {
    const pass = e.target.value;
    setFormData({ ...formData, password: pass });
    checkPasswordStrength(pass);
  };

  const validateStep1 = async () => {
    setError('');
    if (!formData.gamerName.trim()) return setError("Username is required.");
    if (!formData.email.trim() || !/^\S+@\S+\.\S+$/.test(formData.email)) return setError("Valid email is required.");

    setIsCheckingAvailability(true);
    try {
      const response = await fetch(`${BASE_URL}/Auth/check-availability`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-Api-Key': getApiKey() // Fix #1
        },
        body: JSON.stringify({
          gamerName: formData.gamerName,
          email: formData.email
        })
      });
      const data = await response.json();
      if (!data.gamerNameAvailable && !data.emailAvailable) {
        return setError("This username and email are already in use. Please choose different ones.");
      } else if (!data.gamerNameAvailable) {
        return setError("This username is already taken. Please choose a different one.");
      } else if (!data.emailAvailable) {
        return setError("This email is already registered. Please use a different email.");
      }
      setCurrentStep(2);
    } catch (err) {
      setError("Could not verify availability. Please check your connection and try again.");
    } finally {
      setIsCheckingAvailability(false);
    }
  };

  const validateStep2 = () => {
    setError('');
    if (formData.password.length < 6) return setError("Password must be at least 6 characters.");
    if (formData.password !== confirmPassword) return setError("Passwords do not match.");
    if (passwordStrength === 'Weak') return setError("Please choose a stronger password.");
    setCurrentStep(3);
  };

  const handleGoogleCredential = useCallback(async (credentialResponse) => {
    setGoogleLoading(true);
    setError('');
    try {
      const res = await fetch(`${BASE_URL}/auth/google-signin`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Api-Key': getApiKey(), // Fix #1
        },
        body: JSON.stringify({ idToken: credentialResponse.credential }),
      });

      const contentType = res.headers.get('content-type');
      let data = {};
      if (contentType && contentType.includes('application/json')) {
        data = await res.json();
      }

      if (res.ok) {
        if (onSignupSuccess) onSignupSuccess(data);
        navigate('/');
      } else {
        setError(data.message || data.error || 'Google sign-in failed. Please try again.');
      }
    } catch (err) {
      setError('Connection failed. Please check your network.');
    } finally {
      setGoogleLoading(false);
    }
  }, [onSignupSuccess, navigate]);

  const handleGoogleError = useCallback(() => {
    setError('Google sign-in was cancelled or failed.');
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!agreeTerms) return setError("You must agree to the Terms of Service.");
    if (!formData.avatarId) return setError("Please select an avatar.");
    try {
      const response = await fetch(`${BASE_URL}/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-Api-Key': getApiKey() // Fix #1
        },
        body: JSON.stringify(formData)
      });
      let data = {};
      const contentType = response.headers.get("content-type");
      if (contentType && contentType.includes("application/json")) {
        data = await response.json();
      }
      if (response.ok) {
        if (onSignupSuccess && data.accessToken) {
            onSignupSuccess(data);
        }
        setIsSuccess(true);
        setTimeout(() => {
          navigate('/profile');
        }, 3000);
      } else {
        const errorMessage = data.detail || data.message || data.error || `Server Error: ${response.status}`;
        setError(errorMessage);
        if (data.detail && data.detail.toLowerCase().includes("exists")) {
          setCurrentStep(1);
        }
      }
    } catch (err) {
      setError("Connection failed. Please check your network.");
    }
  };

  const Stepper = ({ step }) => {
    const steps = [1, 2, 3];
    return (
      <div className="flex items-center justify-center gap-1 my-8 relative">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-0.5 w-40 bg-gray-100 -z-10" />
        {steps.map((s, index) => {
          const isCompleted = s < step;
          const isActive = s === step;
          return (
            <div key={s} className="flex items-center gap-1 relative z-10">
              {index > 0 && (
                <div className={`h-0.5 w-16 transition-colors duration-300 ${isCompleted ? 'bg-[#FF5E0E]' : 'bg-gray-100'}`} />
              )}
              <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm border-2 transition-all duration-300 ${
                isCompleted ? 'bg-green-500 border-green-500 text-white shadow-lg' :
                isActive ? 'bg-[#FFB800] border-[#FFB800] text-white shadow-xl scale-110' :
                'bg-gray-100 border-gray-100 text-gray-400'
              }`}>
                {isCompleted ? <CheckCircle2 size={20} /> : s}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-transparent p-4 font-sans relative overflow-hidden animate-in zoom-in-95 duration-500">

      {/* Success Popup */}
      {isSuccess && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-white/80 backdrop-blur-md animate-fadeIn">
          <div className="bg-white p-12 rounded-[3rem] shadow-2xl border border-indigo-50 flex flex-col items-center text-center max-w-sm mx-4 animate-scaleUp">
            <div className="w-24 h-24 bg-green-100 text-green-500 rounded-full flex items-center justify-center mb-6 animate-bounce">
              <PartyPopper size={48} />
            </div>
            <h2 className="text-3xl font-black text-slate-800 mb-2">Welcome Aboard!</h2>
            <p className="text-gray-500 font-medium mb-6">Your account is ready. Moving to your profile...</p>
            <div className="w-full bg-gray-100 h-1.5 rounded-full overflow-hidden">
              <div className="bg-green-500 h-full animate-loadingBar"></div>
            </div>
          </div>
        </div>
      )}

      <div className="w-full max-w-lg glass-panel rounded-[2.5rem] p-10 md:p-12 shadow-2xl shadow-game-neon/20 flex flex-col items-center relative transform hover:scale-[1.01] transition-transform duration-500">
        <button
          onClick={() => navigate('/')}
          className="absolute top-6 right-6 p-2 text-game-muted hover:text-white hover:bg-white/10 rounded-full transition-all"
        >
          <X size={24} />
        </button>

        {/* Brand Logo Header */}
        <div className="w-24 h-24 mb-6 flex items-center justify-center relative group">
          <div className="absolute inset-0 bg-gradient-to-tr from-[#FF5E0E] to-[#FFB800] rounded-full blur-xl opacity-35 group-hover:opacity-60 transition-opacity" />
          <img 
            src={logo512} 
            alt="KarnoGames" 
            className="w-full h-full object-contain relative z-10 drop-shadow-[0_8px_20px_rgba(255,94,14,0.45)] transform group-hover:scale-105 transition-transform" 
          />
        </div>

        <h2 className="text-3xl font-heading font-black italic uppercase tracking-tighter text-white mb-2 drop-shadow-[0_0_10px_rgba(255,255,255,0.2)]">Create Account</h2>
        <p className="text-game-muted mb-2 text-center text-sm font-medium">Join the gaming community</p>

        <Stepper step={currentStep} />

        {error && (
          <div className="w-full mb-6 p-4 bg-red-500/10 text-red-400 text-xs rounded-xl border border-red-500/30 font-bold uppercase tracking-tight shadow-[0_0_10px_rgba(239,68,68,0.2)]">
            Error: {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="w-full space-y-6">
          {currentStep === 1 && (
            <div className="space-y-6 animate-fadeIn">
              <div className="space-y-2">
                <label className="text-sm font-bold text-game-muted flex items-center gap-2 uppercase tracking-widest"><User size={16}/> Username</label>
                <input
                  type="text"
                  placeholder="Enter username"
                  value={formData.gamerName}
                  onChange={(e) => setFormData({...formData, gamerName: e.target.value})}
                  className="w-full bg-game-dark/50 border border-game-border text-white rounded-2xl p-4 text-sm outline-none focus:border-game-neon focus:shadow-[0_0_15px_rgba(255,184,0,0.3)] transition-all placeholder:text-gray-600 shadow-inner"
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-bold text-game-muted flex items-center gap-2 uppercase tracking-widest"><Mail size={16}/> Email Address</label>
                <input
                  type="email"
                  placeholder="Enter email address"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  className="w-full bg-game-dark/50 border border-game-border text-white rounded-2xl p-4 text-sm outline-none focus:border-game-neon focus:shadow-[0_0_15px_rgba(255,184,0,0.3)] transition-all placeholder:text-gray-600 shadow-inner"
                />
              </div>

              <button
                type="button"
                onClick={validateStep1}
                disabled={isCheckingAvailability}
                className="game-btn w-full flex items-center justify-center gap-3 py-4 rounded-2xl text-sm shadow-[0_0_20px_rgba(255,94,14,0.4)] disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {isCheckingAvailability ? (
                  <>
                    <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    Checking...
                  </>
                ) : (
                  <>Continue <ArrowRight size={18} /></>
                )}
              </button>
            </div>
          )}

          {currentStep === 2 && (
            <div className="space-y-6 animate-fadeIn">
              <div className="space-y-2">
                <label className="text-sm font-bold text-game-muted flex items-center gap-2 uppercase tracking-widest"><Lock size={16}/> Password</label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    placeholder="Enter password"
                    value={formData.password}
                    onChange={handlePasswordChange}
                    className={`w-full bg-game-dark/50 text-white border border-game-border rounded-2xl p-4 text-sm outline-none transition-all placeholder:text-gray-600 shadow-inner focus:shadow-[0_0_15px_rgba(255,184,0,0.3)] ${
                      formData.password.length > 0 ? (passwordStrength === 'Strong' ? 'border-green-400' : passwordStrength === 'Fair' ? 'border-yellow-400' : 'border-rose-400') : 'focus:border-game-neon'
                    }`}
                  />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400">
                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-bold text-game-muted flex items-center gap-2 uppercase tracking-widest"><Lock size={16}/> Confirm Password</label>
                <div className="relative">
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    placeholder="Confirm password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="w-full bg-game-dark/50 text-white border border-game-border rounded-2xl p-4 text-sm outline-none focus:border-game-neon focus:shadow-[0_0_15px_rgba(255,184,0,0.3)] transition-all placeholder:text-gray-600 shadow-inner"
                  />
                  <button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)} className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white transition-colors">
                    {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
              </div>

              <div className="flex gap-4">
                <button type="button" onClick={() => setCurrentStep(1)} className="flex-1 bg-white/5 border border-white/10 hover:bg-white/10 text-white py-4 rounded-2xl font-black uppercase tracking-widest text-sm transition-all">Back</button>
                <button type="button" onClick={validateStep2} className="flex-1 game-btn py-4 rounded-2xl text-sm shadow-[0_0_20px_rgba(255,94,14,0.4)]">Continue</button>
              </div>
            </div>
          )}

          {currentStep === 3 && (
            <div className="space-y-6 animate-fadeIn">
              <div className="space-y-3">
                <label className="text-sm font-bold text-game-muted flex items-center gap-2 uppercase tracking-widest"><Image size={16}/> Choose Your Avatar</label>
                <div className="grid grid-cols-4 gap-3 bg-game-dark/50 p-4 rounded-[1.5rem] border border-game-border max-h-72 overflow-y-auto shadow-inner">
                  {loadingAvatars ? (
                    <div className="col-span-4 p-8 text-center text-xs text-game-muted font-bold">
                      Loading avatars...
                    </div>
                  ) : avatars.map((avatar) => (
                    <button
                      key={avatar.id}
                      type="button"
                      onClick={() => {
                        setSelectedAvatarId(avatar.id);
                        setFormData({...formData, avatarId: avatar.id});
                      }}
                      className={`relative w-16 h-16 bg-white/5 rounded-xl overflow-hidden border-2 transition-all ${
                        selectedAvatarId === avatar.id ? 'border-game-neon shadow-[0_0_15px_rgba(255,184,0,0.6)] scale-110 z-10' : 'border-transparent opacity-50 hover:opacity-100'
                      }`}
                    >
                      <img src={avatar.url} alt="avatar" className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex items-center gap-3 bg-game-dark/50 p-4 rounded-xl border border-game-border">
                <input type="checkbox" id="agreeTerms" checked={agreeTerms} onChange={(e) => setAgreeTerms(e.target.checked)} className="w-5 h-5 cursor-pointer accent-game-neon rounded" />
                <label htmlFor="agreeTerms" className="text-xs font-medium text-game-muted">
                  I agree to the <a href="/terms" className="text-game-accent font-bold hover:text-game-neon">Terms</a> and <a href="/privacy" className="text-game-accent font-bold hover:text-game-neon">Privacy</a>
                </label>
              </div>

              <div className="flex gap-4 pt-2">
                <button type="button" onClick={() => setCurrentStep(2)} className="flex-1 bg-white/5 border border-white/10 hover:bg-white/10 text-white py-4 rounded-2xl font-black uppercase tracking-widest text-sm transition-all">Back</button>
                <button type="submit" className="flex-1 game-btn py-4 rounded-2xl shadow-[0_0_20px_rgba(255,94,14,0.4)] text-sm">Create Account</button>
              </div>
            </div>
          )}
        </form>

        {/* Google Sign-In Divider */}
        <div className="w-full flex items-center my-6 opacity-50">
          <div className="flex-1 h-[1px] bg-game-border"></div>
          <span className="px-4 text-xs text-game-muted font-bold uppercase tracking-widest">or</span>
          <div className="flex-1 h-[1px] bg-game-border"></div>
        </div>

        {/* Google Sign-In Button */}
        <div className="w-full flex flex-col items-center gap-3 mb-6">
          <p className="text-xs text-game-muted font-semibold uppercase tracking-widest mb-1">Sign up instantly with Google</p>
          {googleLoading ? (
            <div className="w-full flex items-center justify-center gap-3 py-3.5 rounded-full border border-white/10 bg-white/5">
              <div className="w-5 h-5 border-2 border-white/20 border-t-white rounded-full animate-spin"></div>
              <span className="text-sm text-game-muted font-semibold">Signing in with Google...</span>
            </div>
          ) : (
            <div className="w-full flex justify-center py-0.5">
              <GoogleLogin
                onSuccess={handleGoogleCredential}
                onError={handleGoogleError}
                theme="filled_black"
                shape="pill"
                size="large"
                text="signup_with"
                width="380"
                logo_alignment="left"
              />
            </div>
          )}
        </div>

        <p className="text-sm text-game-muted font-medium text-center w-full relative z-10">
          Already have an account? <Link to="/login" className="text-game-neon font-bold hover:underline transition-all drop-shadow-[0_0_5px_rgba(255,184,0,0.3)]">Login</Link>
        </p>
      </div>
    </div>
  );
};

export default Signup;